import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Ban, Building2 } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";

import { Explain } from "./Explain";
import { MARKETING_PATHS } from "./paths";

/**
 * One door, and everything the system knows about it.
 *
 * WHY THIS PAGE EXISTS
 * --------------------
 * The list answers "which doors are there". It cannot answer "why is this one
 * like that", and that question has real consequences: a door might be
 * suppressed because someone reviewed it and ruled it defunct, or because a
 * pipeline heuristic decided its name was unusable, and those are not the same
 * fact. The reasoning existed in `decisions` and was reachable from nowhere.
 *
 * WHAT IT CAN AND CANNOT TELL YOU
 * -------------------------------
 * WHEN a decision was made, and WHAT was decided: both recorded since the
 * beginning. WHO made it: only for decisions recorded in the CRM from now on.
 * The 259 existing decisions came from a review workbook and carry no author,
 * so this page says "unknown" for them rather than implying anybody. That
 * distinction is deliberate -- an attributed guess is worse than an honest gap.
 */

type Detail = {
  id: number;
  mail_to: string | null;
  street: string;
  city: string | null;
  state: string | null;
  zip5: string;
  house_number: string | null;
  cell: string | null;
  wave: string | null;
  region: string | null;
  segment: string | null;
  tenancy: string | null;
  confidence: string | null;
  name_source: string | null;
  is_mailable: boolean;
  is_suppressed: boolean;
  suppression_reason: string | null;
  suppression_source: string | null;
  suppression_note: string | null;
  suppressed_at: string | null;
  status: string;
  company_id: number | null;
  company_name: string | null;
  touches_sent: number;
  last_touch: string | null;
  open_reviews: number;
  decisions_made: number;
  last_decided_on: string | null;
  questions_closed: number;
  created_at: string;
  updated_at: string;
  potential: string | null;
  account_type: string | null;
  rated_on: string | null;
  permit_count: number;
  industry_mix: string | null;
  beyond_dsp: boolean | null;
  dsp_permit: string | null;
  county: string | null;
  /** Everything the DSP operator holds ANYWHERE, which is not the same
   *  question as industry_mix (what stands at this door). See migration 044. */
  operator_industries: string | null;
  operator_makes_wine: boolean | null;
  operator_permits: number | null;
  operator_states: number | null;
};

type Validation = {
  address_id: number;
  status: string;
  canonical_street: string | null;
  canonical_city: string | null;
  canonical_state: string | null;
  canonical_zip5: string | null;
  canonical_zip4: string | null;
  delivery_point: string | null;
  vendor: string | null;
  validated_at: string | null;
  validated_by_name: string | null;
  recheck: boolean | null;
  recheck_reason: string | null;
  street_changed: boolean | null;
  zip_changed: boolean | null;
};

type Permit = {
  permit_number: string;
  owner_name: string | null;
  last_dba: string | null;
  industry_type: string | null;
  status: string | null;
  issued_basis: string | null;
  first_seen: string | null;
  last_seen: string | null;
  missing_since: string | null;
  times_missing: number | null;
  last_street: string | null;
  last_city: string | null;
  last_state: string | null;
  last_zip: string | null;
};

type HistoryRow = {
  id: number;
  decision_key: string;
  type: string;
  answer: string | null;
  value: string | null;
  note: string | null;
  decided_on: string | null;
  created_at: string;
  is_applied: boolean;
  decided_by_name: string | null;
  question: string | null;
  why: string | null;
};

type OpenRow = {
  id: number;
  type: string;
  question: string | null;
  why: string | null;
  days_open: number | null;
  first_seen: string | null;
};

type Mailing = {
  id: number;
  touch: string;
  cell: string;
  status: string;
  mail_date: string | null;
  vendor: string | null;
  vendor_order_id: string | null;
};

const fmtDate = (v: string | null | undefined) =>
  v
    ? new Date(v.length <= 10 ? `${v}T00:00:00` : v).toLocaleDateString(
        undefined,
        {
          year: "numeric",
          month: "short",
          day: "numeric",
        },
      )
    : "—";

const REASONS = [
  { value: "requested", label: "Asked not to be mailed" },
  { value: "returned", label: "Mail returned" },
  { value: "deceased", label: "Deceased" },
  { value: "duplicate", label: "Duplicate of another door" },
];
const SOURCES = ["phone", "manual", "web", "pcm", "stannp", "resend"];

export const DoorDetail = () => {
  const { id } = useParams();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [door, setDoor] = useState<Detail | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [open, setOpenItems] = useState<OpenRow[]>([]);
  const [mailings, setMailings] = useState<Mailing[]>([]);
  const [permits, setPermits] = useState<Permit[]>([]);
  const [validation, setValidation] = useState<Validation | null>(null);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const { data } = await dataProvider.getOne("door_detail", {
        id: Number(id),
      });
      setDoor(data as Detail);
    } catch {
      setMissing(true);
      return;
    }
    const list = async <T,>(resource: string, filter: object, sort: string) => {
      try {
        const { data } = await dataProvider.getList(resource, {
          filter,
          pagination: { page: 1, perPage: 100 },
          sort: { field: sort, order: "DESC" },
        });
        return data as T[];
      } catch {
        return [] as T[];
      }
    };
    setHistory(
      await list<HistoryRow>(
        "door_review_history",
        { address_id: Number(id) },
        "created_at",
      ),
    );
    setOpenItems(
      await list<OpenRow>(
        "review_backlog",
        { address_ref: Number(id) },
        "first_seen",
      ),
    );
    setMailings(
      await list<Mailing>("mailings", { address_id: Number(id) }, "touch"),
    );
    setPermits(
      await list<Permit>(
        "door_permits",
        { address_id: Number(id) },
        "permit_number",
      ),
    );
    const v = await list<Validation>(
      "door_validation",
      { address_id: Number(id) },
      "validated_at",
    );
    setValidation(v[0] ?? null);
  }, [dataProvider, id]);

  useEffect(() => {
    load().catch((e) => notify(String(e?.message ?? e), { type: "error" }));
  }, [load, notify]);

  if (missing) {
    return (
      <div className="space-y-3">
        <BackLink />
        <p className="text-sm text-muted-foreground">No door with id {id}.</p>
      </div>
    );
  }
  if (!door) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4">
      <BackLink />

      <div>
        <h1 className="text-xl font-semibold">{door.mail_to ?? "—"}</h1>
        <p className="text-sm text-muted-foreground">
          {door.street}
          {door.city ? `, ${door.city}` : ""}
          {door.state ? `, ${door.state}` : ""} {door.zip5}
        </p>
        <div className="flex flex-wrap gap-1 mt-2">
          <Badge variant={door.is_suppressed ? "destructive" : "outline"}>
            {door.status}
          </Badge>
          {door.cell && <Badge variant="outline">{door.cell}</Badge>}
          {door.wave && <Badge variant="outline">{door.wave}</Badge>}
          {door.company_id && (
            <Badge variant="outline">
              <Link
                to={`/companies/${door.company_id}/show`}
                className="underline"
              >
                {door.company_name ?? "company"}
              </Link>
            </Badge>
          )}
        </div>
      </div>

      {/*
        HOW THE PIPELINE JUDGED THIS DOOR.

        These four are not decoration: `confidence` and `name_source` are why a
        door has the addressee it has, and they are the difference between a
        name read off a permit and one inferred. When a review question asks
        "is this a real distillery", this is the evidence it was raised from.
      */}
      <Card>
        <CardContent className="pt-4">
          <h2 className="text-sm font-medium mb-3">How the pipeline read it</h2>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Field
              label={<Explain term="name_source">Name source</Explain>}
              value={door.name_source}
              /* The only one of the three that is a human judgment, so it is
                 the only one worth drawing the eye to. */
              highlight={door.name_source === "override"}
            />
            <Field
              label={<Explain term="tenancy">Tenancy</Explain>}
              value={door.tenancy}
              highlight={door.tenancy === "shared_address"}
            />
            <Field label="Region" value={door.region} />
            <Field label="House number" value={door.house_number} />
            <Field label="First seen" value={fmtDate(door.created_at)} />
            <Field label="Last updated" value={fmtDate(door.updated_at)} />
          </dl>

          {door.name_source === "override" && (
            <p className="text-xs mt-3">
              <b>Someone chose this name by hand.</b> It came from a decision,
              not from the permit, which is why a pipeline rebuild will not
              revert it.
            </p>
          )}

          {/*
            `confidence` and `segment` are deliberately absent.

            Every one of the 5,363 doors reads `confirmed` and
            `distillery_plant`. A field with one value across the whole table
            looks like a score and discriminates nothing; printing it on every
            page would imply this door had been assessed and passed, when in
            truth nothing was ever assessed. If either ever gains a second
            value, it earns a row here.
          */}
        </CardContent>
      </Card>

      {/*
        THE HUMAN RATING.

        `potential` and `account_type` come from `prospects` -- the ratings made
        in the old HTML review page. Only 26 of 5,363 doors carry one, so having
        no rating is the NORMAL case and must not read as a verdict: this shows
        "not rated", never "low".
      */}
      <PromotionCard door={door} />

      <RatingCard door={door} onChanged={load} />

      <ValidationCard door={door} validation={validation} onChanged={load} />

      {/* ── permits ── */}
      <Card>
        <CardContent className="pt-4 space-y-2">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-medium">TTB permits</h2>
            <span className="text-xs text-muted-foreground">
              {door.permit_count} permit{door.permit_count === 1 ? "" : "s"}
              {door.industry_mix ? ` · ${door.industry_mix}` : ""}
              {door.county ? ` · ${door.county} County` : ""}
            </span>
          </div>

          {/*
            THE OPERATOR'S WIDER FOOTPRINT, and it is a different question.

            `industry_mix` above reports the permits standing AT THIS DOOR, and
            reports them truthfully -- but the derivation only ever indexed
            Distilled Spirits Plant, Wholesaler and Importer, so a company that
            also runs a winery reads as spirits-only here. 1,091 of 5,264 active
            DSP owners (21%) hold a Wine Producer permit; 1,235 doors are
            affected. E. & J. Gallo reads "Distilled Spirits Plant" at the door
            and holds 37 permits across 3 states.

            Rendered only when it says something the line above does not, so an
            ordinary one-permit distillery gains no noise.
          */}
          {door.operator_industries &&
            door.operator_industries !== door.industry_mix && (
              <p className="text-xs text-muted-foreground">
                Operator holds{" "}
                <b className="text-foreground">
                  {door.operator_permits} permit
                  {door.operator_permits === 1 ? "" : "s"}
                </b>{" "}
                across {door.operator_states} state
                {door.operator_states === 1 ? "" : "s"} ·{" "}
                {door.operator_industries}
                {door.operator_makes_wine && (
                  <span className="ml-1 font-medium text-foreground">
                    — also a wine producer
                  </span>
                )}
              </p>
            )}
          {permits.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No permit is linked to this address.
            </p>
          ) : (
            <div className="border rounded-md divide-y">
              {permits.map((p) => (
                <div key={p.permit_number} className="p-2 text-sm">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-medium">
                      {p.permit_number}
                    </span>
                    <Badge variant="secondary">{p.industry_type ?? "—"}</Badge>
                    <Badge
                      variant={
                        p.status === "active" ? "outline" : "destructive"
                      }
                    >
                      {p.status ?? "unknown"}
                    </Badge>
                    {p.times_missing ? (
                      // A permit that vanished from TTB's list and came back is
                      // a different thing from one that never moved, and it is
                      // often what a review question is really about.
                      <Badge variant="outline" className="border-orange-400">
                        missing {p.times_missing}x
                        {p.missing_since ? ` · since ${p.missing_since}` : ""}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {p.last_dba && p.last_dba !== p.owner_name ? (
                      <>
                        <b>{p.last_dba}</b> · owner {p.owner_name ?? "—"}
                      </>
                    ) : (
                      <>owner {p.owner_name ?? "—"}</>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {[p.last_street, p.last_city, p.last_state, p.last_zip]
                      .filter(Boolean)
                      .join(", ") || "no address on the permit"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    first seen {p.first_seen ?? "—"} · last seen{" "}
                    {p.last_seen ?? "—"}
                    {p.issued_basis ? ` · ${p.issued_basis}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── the review record ── */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <h2 className="text-sm font-medium">Review</h2>
            <span className="text-xs text-muted-foreground">
              {door.decisions_made} decision
              {door.decisions_made === 1 ? "" : "s"}
              {door.last_decided_on
                ? ` · last ${fmtDate(door.last_decided_on)}`
                : ""}
              {door.open_reviews > 0 ? ` · ${door.open_reviews} open` : ""}
            </span>
          </div>

          {open.length > 0 && (
            <div className="space-y-2">
              {open.map((q) => (
                <div
                  key={q.id}
                  className="rounded-md border border-orange-400/50 p-2 text-sm"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="border-orange-400">
                      open · {q.type}
                    </Badge>
                    {q.days_open != null && (
                      <span className="text-xs text-muted-foreground">
                        waiting {q.days_open} day{q.days_open === 1 ? "" : "s"}
                      </span>
                    )}
                  </div>
                  <div className="mt-1">{q.question ?? "—"}</div>
                  {q.why && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {q.why}
                    </div>
                  )}
                </div>
              ))}
              <Button asChild variant="outline" size="sm">
                <Link to={MARKETING_PATHS.review}>
                  Answer in the review queue
                </Link>
              </Button>
            </div>
          )}

          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No decision has ever been recorded for this door.
            </p>
          ) : (
            <div className="border rounded-md divide-y">
              {history.map((h) => (
                <div key={h.id} className="p-2 text-sm">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary">{h.type}</Badge>
                    <b>{h.answer ?? "—"}</b>
                    {h.value && (
                      <span className="text-muted-foreground">{h.value}</span>
                    )}
                    {!h.is_applied && (
                      // Decided and applied are different claims. A decision
                      // sits in the ledger until the pipeline acts on it, and
                      // that gap is exactly where "I already answered that" and
                      // "the list still says otherwise" live.
                      <Badge variant="outline" className="border-orange-400">
                        not yet applied
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {fmtDate(h.decided_on ?? h.created_at)} ·{" "}
                    {h.decided_by_name ? (
                      <>by {h.decided_by_name}</>
                    ) : (
                      // Not "by nobody". These predate the CRM recording an
                      // author, and naming someone would be inventing it.
                      <span title="Recorded before the CRM captured an author">
                        author unknown
                      </span>
                    )}
                  </div>
                  {h.question && (
                    <div className="text-xs text-muted-foreground mt-1">
                      asked: {h.question}
                    </div>
                  )}
                  {h.note && (
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {h.note}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── mail ── */}
      <Card>
        <CardContent className="pt-4 space-y-2">
          <h2 className="text-sm font-medium">Mail</h2>
          {mailings.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing has been planned or sent to this door.
            </p>
          ) : (
            <div className="border rounded-md divide-y">
              {mailings.map((m) => (
                <div
                  key={m.id}
                  className="p-2 text-sm flex flex-wrap items-center gap-2"
                >
                  <span className="font-mono">{m.touch}</span>
                  <Badge
                    variant={m.status === "planned" ? "outline" : "secondary"}
                  >
                    {m.status}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {m.cell}
                    {m.mail_date ? ` · ${fmtDate(m.mail_date)}` : ""}
                    {m.vendor ? ` · ${m.vendor}` : ""}
                    {m.vendor_order_id ? ` · order ${m.vendor_order_id}` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <OptOut door={door} onChanged={load} />
    </div>
  );
};

/**
 * The rating, editable in place.
 *
 * Writes through `rate_door()` rather than to `prospects` directly: the natural
 * key, the DSP permit lookup and the vocabulary check all live in one place, so
 * a rating made here is indistinguishable from one made anywhere else.
 *
 * NOT a column on `addresses`. That table is rebuilt from TTB text every run,
 * and a judgment stored there would be overwritten by the next sync -- which
 * is the whole reason overrides, decisions and prospects are separate tables.
 *
 * "Unrated" is offered as a real choice, not just an initial state. 5,337 of
 * 5,363 doors are unrated; being able to say "I looked and I am not calling it"
 * and being unable to undo a mis-click are the same feature.
 */
const RatingCard = ({
  door,
  onChanged,
}: {
  door: Detail;
  onChanged: () => Promise<void>;
}) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [potential, setPotential] = useState(door.potential ?? "unrated");
  const [accountType, setAccountType] = useState(
    door.account_type ?? "unrated",
  );

  // The row can be reloaded under this card (after a save, or a navigation
  // between doors), so the controls follow the record rather than keeping a
  // stale first render.
  useEffect(() => {
    setPotential(door.potential ?? "unrated");
    setAccountType(door.account_type ?? "unrated");
  }, [door.potential, door.account_type, door.id]);

  const dirty =
    potential !== (door.potential ?? "unrated") ||
    accountType !== (door.account_type ?? "unrated");

  const save = async () => {
    setBusy(true);
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { error } = await getSupabaseClient().rpc("rate_door", {
        p_address_id: door.id,
        // "unrated" is the UI's word for null. Sending the literal string would
        // fail the vocabulary check in rate_door, which is the point of having
        // one.
        p_potential: potential === "unrated" ? null : potential,
        p_account_type: accountType === "unrated" ? null : accountType,
        p_note: null,
      });
      if (error) throw new Error(error.message);
      notify("Rating saved.", { type: "info" });
      await onChanged();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-medium">Rating</h2>
          <span className="text-xs text-muted-foreground">
            {door.rated_on ? `rated ${fmtDate(door.rated_on)}` : "never rated"}
          </span>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <div className="text-xs text-muted-foreground mb-1">
              <Explain term="potential">Potential</Explain>
            </div>
            <Select value={potential} onValueChange={setPotential}>
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="unrated">Not rated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Type</div>
            <Select value={accountType} onValueChange={setAccountType}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="distiller">distiller</SelectItem>
                <SelectItem value="bottler_ndp">bottler / NDP</SelectItem>
                <SelectItem value="other">other</SelectItem>
                <SelectItem value="na">not applicable</SelectItem>
                <SelectItem value="unrated">unrated</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={save} disabled={busy || !dirty}>
            {busy ? "Saving…" : "Save rating"}
          </Button>
          {door.beyond_dsp && (
            // Derived from the permits, not chosen: a distillery that also
            // imports or wholesales is a bigger operation than one that only
            // distils. 908 of the 5,360 doors qualify.
            <Badge variant="outline" className="border-foreground">
              also imports or wholesales
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Has anyone checked this address is real?
 *
 * WHY IT IS WORTH ITS OWN CARD
 * ----------------------------
 * The whole list was built from TTB's published text and never checked against
 * the postal file. Stannp charges $0.20 extra per unverified piece and can
 * decline to mail them, so on 5,360 doors this is money, not tidiness.
 *
 * A BARE "VALID" HIDES THE INTERESTING HALF. The validator can call an address
 * deliverable while quietly rewriting the street or the ZIP to get there -- and
 * that rewrite is the thing worth seeing, because it means what we hold is not
 * what the Post Office recognizes. `street_changed` and `zip_changed` come from
 * the view for exactly that reason.
 */
const ValidationCard = ({
  door,
  validation,
  onChanged,
}: {
  door: Detail;
  validation: Validation | null;
  onChanged: () => Promise<void>;
}) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { data, error } = await getSupabaseClient().functions.invoke(
        "validate-addresses",
        { body: { limit: 1, address_id: door.id } },
      );
      // supabase-js discards the body of any non-2xx and reports it as a
      // transport error; the real answer is on error.context. Same recovery as
      // sendClient, for the same reason.
      if (error) {
        const ctx = (error as { context?: Response }).context;
        const body = ctx?.json ? await ctx.json().catch(() => null) : null;
        throw new Error(body?.error ?? error.message);
      }
      notify((data as { note?: string })?.note ?? "Validation run.", {
        type: "info",
      });
      await onChanged();
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const tone =
    validation?.status === "validated"
      ? "secondary"
      : validation?.status === "undeliverable"
        ? "destructive"
        : "outline";

  return (
    <Card>
      <CardContent className="pt-4 space-y-2">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <h2 className="text-sm font-medium">Address validation</h2>
          {validation?.validated_at && (
            // "by" is load-bearing. Without it the person's name sits in a row
            // of statuses and reads as one -- a profile still showing Atomic
            // CRM's placeholder renders as "pcm · Pending Pending", which looks
            // like the validation is pending when it is complete.
            <span className="text-xs text-muted-foreground">
              {fmtDate(validation.validated_at)}
              {validation.vendor ? ` · ${validation.vendor}` : ""}
              {validation.validated_by_name
                ? ` · by ${validation.validated_by_name}`
                : " · automated run"}
            </span>
          )}
        </div>

        {!validation ? (
          <p className="text-sm text-muted-foreground">
            Never checked against the postal file.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Badge
                variant={tone}
                title={
                  validation.vendor === "pcm" &&
                  validation.status === "validated"
                    ? "PCM put this address in its `valid` group and echoed it back unchanged. It returned no ZIP+4 and no delivery point, so this is not evidence of a CASS/DPV match to a specific mailbox."
                    : undefined
                }
              >
                {validation.status}
              </Badge>
              {validation.delivery_point && (
                <Badge variant="outline">DP {validation.delivery_point}</Badge>
              )}
              {validation.street_changed && (
                <Badge variant="outline" className="border-orange-400">
                  street rewritten
                </Badge>
              )}
              {validation.zip_changed && (
                <Badge variant="outline" className="border-orange-400">
                  ZIP rewritten
                </Badge>
              )}
            </div>
            {(validation.street_changed || validation.zip_changed) && (
              <p className="text-xs">
                Postal form:{" "}
                <b>
                  {[
                    validation.canonical_street,
                    validation.canonical_city,
                    validation.canonical_state,
                    validation.canonical_zip5 +
                      (validation.canonical_zip4
                        ? `-${validation.canonical_zip4}`
                        : ""),
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </b>
              </p>
            )}
            {validation.recheck_reason && (
              <p className="text-xs text-muted-foreground">
                {validation.recheck_reason}
              </p>
            )}
            {validation.vendor === "pcm" &&
              validation.status === "validated" &&
              !validation.canonical_zip4 &&
              !validation.delivery_point && (
                // Said out loud, because "validated" invites more confidence
                // than the response supports. PCM returned the address exactly
                // as sent, with no ZIP+4 and no delivery point -- a CASS/DPV
                // pass returns both. So this says the address is well formed
                // and consistent, not that the Post Office has confirmed a
                // mailbox behind it.
                <p className="text-xs text-muted-foreground">
                  PCM accepted this address unchanged and returned no ZIP+4 or
                  delivery point, so it is a well-formed-address check rather
                  than proof of a deliverable mailbox.
                </p>
              )}
          </>
        )}

        <Button variant="outline" size="sm" onClick={run} disabled={busy}>
          {busy
            ? "Checking…"
            : validation
              ? "Check again"
              : "Validate this address"}
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * Is this door a company in the CRM yet, and a way to make it one.
 *
 * WHAT "PROMOTED" MEANS HERE
 * --------------------------
 * A door is a mailing address the pipeline owns. A company is a CRM record a
 * person owns -- notes, contacts, deals. Promotion creates the second from the
 * first and links them with `companies.address_id`, so the mail history stays
 * attached to whatever the relationship becomes.
 *
 * The link is a bigint FK to `addresses(id)`, deliberately, and never street
 * text: the previous scheme hashed the street, which drifts every time TTB
 * re-types it or the set of permits at a building changes.
 *
 * The state is shown even when there is nothing to do, because "already
 * promoted" is the answer to a question people actually ask of this page, and a
 * button that silently turns into a link answers it only by implication.
 */
const PromotionCard = ({ door }: { door: Detail }) => (
  <Card>
    <CardContent className="pt-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <h2 className="text-sm font-medium">CRM company</h2>
        <Badge variant={door.company_id ? "secondary" : "outline"}>
          {door.company_id ? "promoted" : "not promoted"}
        </Badge>
      </div>

      {door.company_id ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm">{door.company_name ?? "Company"}</span>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/companies/${door.company_id}/show`}>
              <Building2 className="size-4" /> Open company
            </Link>
          </Button>
        </div>
      ) : (
        <>
          <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
            This door is on the mailing list and has no CRM record. Promoting
            creates a company pre-filled from this address and links the two, so
            its mail history follows the relationship.
          </p>
          <Button variant="outline" size="sm" asChild>
            <Link
              to="/companies/create"
              // The address travels in router state rather than the URL: it is
              // a whole record, and putting a name and street in a query string
              // would both be ugly and leak them into history and referrers.
              state={{
                record: {
                  name: door.mail_to ?? "",
                  address: door.street,
                  city: door.city ?? "",
                  state_abbr: door.state ?? "",
                  zipcode: door.zip5,
                  country: "USA",
                  address_id: door.id,
                },
              }}
            >
              <Building2 className="size-4" /> Promote to a company
            </Link>
          </Button>
        </>
      )}
    </CardContent>
  </Card>
);

const BackLink = () => (
  <Button asChild variant="ghost" size="sm" className="-ml-2">
    <Link to={MARKETING_PATHS.doors}>
      <ArrowLeft className="size-4" /> All doors
    </Link>
  </Button>
);

const Field = ({
  label,
  value,
  highlight,
}: {
  label: ReactNode;
  value: string | null | undefined;
  highlight?: boolean;
}) => (
  <div>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className={highlight ? "font-medium underline" : "font-medium"}>
      {value || "—"}
    </dd>
  </div>
);

/**
 * The opt-out, which lives HERE and nowhere else now.
 *
 * It used to be a one-click ⊘ in the list, beside a promote button it looked
 * exactly like. An opt-out is permanent and honored for post and email
 * forever; nothing that permanent should sit one mis-click away in a table of
 * 5,360 rows, where the row under the cursor moves when a filter loads.
 *
 * On this page the door is already identified, its history is on screen, and
 * the act is deliberate by construction.
 *
 * It is CONSENT, not data quality. A mail drop or a defunct permit is
 * overrides.suppress_address and belongs in the Review queue; conflating them
 * would make an opt-out look reversible by a pipeline rebuild, which it must
 * never be.
 */
const OptOut = ({
  door,
  onChanged,
}: {
  door: Detail;
  onChanged: () => Promise<void>;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("requested");
  const [source, setSource] = useState("phone");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (door.is_suppressed) {
    return (
      <Card>
        <CardContent className="pt-4">
          <h2 className="text-sm font-medium mb-1">Opted out</h2>
          <p className="text-sm text-muted-foreground">
            {door.suppression_reason ?? "no reason recorded"}
            {door.suppression_source ? ` · via ${door.suppression_source}` : ""}
            {door.suppressed_at ? ` · ${fmtDate(door.suppressed_at)}` : ""}
          </p>
          {door.suppression_note && (
            <p className="text-sm mt-1">{door.suppression_note}</p>
          )}
          <p className="text-xs text-muted-foreground mt-2">
            This is permanent and is honored for post and email. It is not
            undone by a pipeline rebuild, and there is deliberately no button
            here to reverse it.
          </p>
        </CardContent>
      </Card>
    );
  }

  const submit = async () => {
    setBusy(true);
    try {
      await dataProvider.create("suppressions", {
        data: {
          address_id: door.id,
          reason,
          source,
          note: note.trim() || null,
        },
      });
      setOpen(false);
      notify(`${door.mail_to ?? "This door"} will not be mailed again`, {
        type: "info",
      });
      await onChanged();
    } catch (e) {
      notify(`Could not record the opt-out: ${(e as Error).message}`, {
        type: "error",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <h2 className="text-sm font-medium mb-1">Opt out</h2>
        <p className="text-xs text-muted-foreground mb-3 max-w-2xl">
          Records that this door asked not to be mailed. Permanent, honored for
          post and email, and not reversed by a pipeline rebuild. For a bad
          address or a defunct permit use the review queue instead — that is
          data quality, not consent, and it should stay reversible.
        </p>
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Ban className="size-4" /> Record an opt-out
        </Button>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Opt out {door.mail_to ?? "this door"}</DialogTitle>
              <DialogDescription>
                This is permanent and is honored for post and email. It cannot
                be undone here.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Reason</div>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">
                  Heard via
                </div>
                <Select value={source} onValueChange={setSource}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((sv) => (
                      <SelectItem key={sv} value={sv}>
                        {sv}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Note</div>
                <Input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="who said so, and when"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button variant="destructive" onClick={submit} disabled={busy}>
                Record opt-out
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
